import React from 'react';
import { StaticImage } from 'gatsby-plugin-image';
import { IStaticImageProps } from 'gatsby-plugin-image/dist/src/components/static-image.server';

import { TDynamicAdOptions } from '../BannerAdStatic';

const useBannerAdStatic = (
  version: 'howToWatch' | 'spectrumGoes' | 'dynamic',
  dynamicOptions?: TDynamicAdOptions[],
  isGetStarted?: boolean,
  visitId?: string,
  buyflowLink?: string,
  contextToken?: string
) => {
  const appleLink = isGetStarted
    ? 'https://apps.apple.com/app/apple-store/id420455839?pt=431805&ct=PURL_appstore&mt=8'
    : 'https://apps.apple.com/app/apple-store/id420455839?pt=431805&ct=SOD_STVA_0221&mt=8';

  const googleLink = isGetStarted
    ? 'https://play.google.com/store/apps/details?id=com.TWCableTV&referrer=utm_source%3DPURL%26utm_medium%3Dwelcome'
    : 'https://play.google.com/store/apps/details?id=com.TWCableTV&referrer=utm_source%3DSOD_STVA%26utm_medium%3Dbanner%26utm_term%3DSTVA%26utm_campaign%3DSOD_STVA';

  const imageProps: Partial<IStaticImageProps> = {
    width: 450,
    height: 300,
    placeholder: 'blurred',
    transformOptions: {
     fit: 'inside'
    }
  };

  const ads = {
    hboMax: {
      image: (
        <StaticImage
          src="../../../../static/images/ads/hbomax.png"
          alt="A TV Screen showing a scene from 'Sinners' and the new 'HBO Max' logo"
          {...imageProps}
        />
      ),
      title: 'HBO Max',
      content:
        'The one to watch for HBO hit series, movies, reality, & more. Enjoy the HBO Max app on all your favorite devices, plus watch HBO Channels live and On Demand with Spectrum.',
      linkPath: `${buyflowLink}&visitId=${visitId}${contextToken}`,
    },
    spectrumMobile: {
      image: (
        <StaticImage
          src="../../../../static/images/ads/spectrumMobileAd.png"
          alt="A TV Screen showing 'Watchmen' and a tablet showing 'The Big Bang Theory'"
          {...imageProps}
        />
      ),
      title: 'Switch and Save with Spectrum Mobile',
      content: (
        <p className="banner-ad-item__description">
          Flexibile data plans with contracts, no added taxes and no hidden
          fees.{' '}
          <a
            href={`https://www.spectrum.com/mobile/calculator?cmp=sod_mob60_bbanner_0622&visitId=${visitId}${contextToken}`}
            target="_blank"
            rel="noreferrer"
            style={{ display: 'block' }}
          >
            See My Savings
          </a>
        </p>
      ),
    },
    onDemandTV: {
      image: (
        <StaticImage
          src="../../../../static/images/ads/on-demand-tv.png"
          alt="A TV Screen displaying Harry Potter with a remote standing next to it."
          {...imageProps}
        />
      ),
      title: 'Enjoy On Demand',
      content: 'Thousands of On Demand titles available anytime',
    },
    onDemandLaptop: {
      image: (
        <StaticImage
          src="../../../../static/images/ads/on-demand-laptop.png"
          alt="A Laptop showing Spectrum.tv content"
          {...imageProps}
        />
      ),
      title: 'Watch Online',
      content: (
        <p className="banner-ad-item__description">
          Enjoy your favorite movies and shows on laptops, tablets and other
          mobile devices at{' '}
          <a
            href={`https://watch.spectrum.net/?visitId=${visitId}${contextToken}`}
            target="_blank"
            rel="noreferrer"
          >
            SpectrumTV.com
          </a>
        </p>
      ),
      linkPath: `https://watch.spectrum.net/?visitId=${visitId}${contextToken}`,
    },

    onDemandTablet: {
      image: (
        <StaticImage
          src="../../../../static/images/ads/on-demand-tablet.png"
          alt="A Tablet showing Spectrum.tv content"
          {...imageProps}
        />
      ),
      title: 'Get the Spectrum TV App',
      content: (
        <>
          <p className="banner-ad-item__description">
            Watch live TV and On Demand anywhere in your home with our mobile
            app.
          </p>
          <div className="banner-ad-item__buttons">
            <a
              href={appleLink}
              className="button"
              title="Download on the Apple App Store"
              aria-label="Download on the Apple App Store"
            >
              <StaticImage
                src="../../../../static/images/ads/badge-apple-app-store.png"
                alt=""
                width={150}
                height={50}
                placeholder="blurred"
              />
            </a>
            <a
              href={googleLink}
              className="button"
              title="Download on the Apple App Store"
              aria-label="Download on the Google Play Store"
            >
              <StaticImage
                src="../../../../static/images/ads/badge-google-play.png"
                alt=""
                width={142}
                height={42}
                placeholder="blurred"
              />
            </a>
          </div>
        </>
      ),
      linkPath: `https://www.spectrum.net/page/spectrum-tv-app?visitId=${visitId}${contextToken}`,
    },
    appGrid: {
      image: (
        <StaticImage
          src="../../../../static/images/ads/app-grid.png"
          alt="A grid of app icons including, Max, ABC, Nick Jr., NBC, TBS, and E!"
          {...imageProps}
        />
      ),
      title: 'Take TV Anywhere',
      content:
        'Download apps from your favorite networks and watch anytime on your tablet and smartphone on-the-go.',
      linkPath: `https://www.spectrum.net/support/tv/tv-channel-apps?visitId=${visitId}${contextToken}`,
    },
  };

  const { hboMax, onDemandTV, onDemandLaptop, onDemandTablet, appGrid } = ads;

  const versions = {
    spectrumGoes: [hboMax, onDemandLaptop, onDemandTablet, appGrid],
    howToWatch: [onDemandTV, onDemandLaptop, onDemandTablet],
    dynamic: dynamicOptions?.map((option) => ads[option]),
  };

  return versions[version];
};

export default useBannerAdStatic;
